import React, {FC, useCallback, useEffect, useId, useRef, useState} from 'react';
import {RemovedMarker, Route} from './Route';
import {Map as GoogleMap, useMapsLibrary, useMap} from '@vis.gl/react-google-maps';
import {Marker} from './Marker';
import {eventHandlerMap, routeUpdater, getADPmapSettings} from '../../utils';
import {InfoWindowType, MarkerType, RouteType, MapSettings} from '../../types';
import styles from './styles.module.scss';
import {useDNDContext} from '../Table/DNDController/useDNDContext';
import {DNDState} from '../Table/DNDController/DNDContext';
import {Button, Flex, Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {texts} from '../../texts';
import {HamburgerMenu} from '../HamburgerMenu';
import {MarkerIconWithWrapper} from './Marker/MarkerIconWithWrapper';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {createPortal} from 'react-dom';
import {updateADPmapSettings} from '../../services/serverDataStore';
import {DND_PLACEHOLDER_HEIGHT} from '../../constants';

export type MapProps = {
    mapID?: string;
    dndState: DNDState;
    id: string;
    selectedLocations?: MarkerType[];
    isShowLocations?: boolean;
    setIsScreenLocked: (value: boolean) => void;
    routes: RouteType[];
    setRoutes: (routes: RouteType[]) => void;
    mapSettings: MapSettings;
    setMapSettings: (settings: MapSettings) => void;
};

type Coordinates = {lat: number; lng: number};

const center: Coordinates = {lat: 51.505, lng: -0.09};

export const Map = ({
    dndState,
    id,
    mapID,
    selectedLocations,
    isShowLocations,
    setIsScreenLocked,
    routes,
    setRoutes,
    mapSettings,
    setMapSettings
}: MapProps) => {
    const [markers, setMarkers] = useState<Array<MarkerType>>([]);

    const [hoveredMarker, setHoveredMarker] = useState<string>();
    // TODO: I'm not sure if it's really necessary to have this state
    // and use zoomfactor function in routes updates
    //especially if needs to be a state
    // check when drag and drop is implemented
    const [zoom, setZoom] = useState(12);

    const [currentCenter, setCurrentCenter] = useState<Coordinates>();

    const MAP_CONTAINER_ID = useId();

    const wrapperRef = useRef<HTMLDivElement>(null);

    const directionsRenderers = useRef<
        React.MutableRefObject<{[key: string]: google.maps.DirectionsRenderer}> | {}
    >({});

    const geometryLibrary = useMapsLibrary('geometry');
    const map = useMap();

    useEffect(() => {
        getADPmapSettings().then(settings => {
            if (settings) {
                setMapSettings(settings);
            }
        });
    }, []);

    const setAndFitMarkerBounds = useCallback(
        (value: React.SetStateAction<MarkerType[]>) => {
            const newMarkers = Array.isArray(value) ? value : value(markers);

            setMarkers(value);

            if (!map || newMarkers.length === 0) return;

            const bounds = new google.maps.LatLngBounds();

            newMarkers.forEach(marker => {
                if (marker.lat && marker.lng) {
                    bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
                }
            });

            if (!bounds.isEmpty()) {
                map.fitBounds(bounds);
            }
        },
        [map, markers]
    );

    const onShowStartEndHandler = async () => {
        toggleShowStartEnd(!mapSettings.showStartEnd);
    };

    useEffect(() => {
        updateMarkersArray();
    }, [mapSettings.showStartEnd]);

    const updateMarkersArray = () => {
        const selectedRoutes = routes.filter(route => route.isSelected);
        if (selectedRoutes || selectedLocations) {
            setAndFitMarkerBounds([
                ...selectedRoutes
                    ?.map(route => route.markers)
                    .flat()
                    .map(marker => ({
                        ...marker,
                        selected: markers.find(m => m.id === marker.id)?.selected
                    })),
                ...(mapSettings.showStartEnd
                    ? selectedRoutes
                          .flatMap(route => [route.startMarker, route.endMarker])
                          .filter(Boolean)
                    : []),
                ...selectedLocations
                    .map(marker => (isShowLocations ? {...marker, color: '#656C73'} : null)) // Same color as point background color in table
                    .filter(Boolean)
            ]);
        }
    };

    useEffect(() => {
        updateMarkersArray();
    }, [routes, selectedLocations, isShowLocations]);

    useEffect(() => {
        if (dndState && !dndState.isMoving && dndState.coordinates) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();

            if (
                wrapperRect.left <= dndState.coordinates[0] &&
                wrapperRect.top <= dndState.coordinates[1] &&
                wrapperRect.bottom >= dndState.coordinates[1] &&
                wrapperRect.right >= dndState.coordinates[0]
            ) {
                console.log('setTo', id);
                if (hoveredMarker) {
                    const marker = markers.find(x => x.id === hoveredMarker);
                    dndState.setTo(prev => ({
                        ...prev,
                        id,
                        group: marker.route.id,
                        row: Number.parseInt(marker.label)
                    }));
                    setHoveredMarker(undefined);
                } else {
                    dndState.setTo(prev => ({...prev, id}));
                }
            }
        }
    }, [dndState?.isMoving, dndState?.coordinates]);

    const removeRoute = (removedMarker: RemovedMarker) => {
        const changedRoute = routes.find(route => route.title === removedMarker.routeId);

        if (removedMarker.routeId === undefined) {
            setAndFitMarkerBounds(prev =>
                prev.filter(marker => marker.id !== removedMarker.pointId)
            );
            return;
        }
        if (changedRoute?.markers.length === 2) {
            setRoutes(routes.filter(route => route.title !== removedMarker.routeId));
            // TODO: not sure about this Bogdan's hack
            // check after D&D is implemented
            if (directionsRenderers.current[removedMarker.routeId]) {
                (
                    directionsRenderers.current[
                        removedMarker.routeId
                    ] as google.maps.DirectionsRenderer
                ).setMap(null);
                delete directionsRenderers.current[removedMarker.routeId];
            }
        } else {
            const newRoutes = routes.map(route => {
                if (route.title === removedMarker.routeId) {
                    return {
                        ...route,
                        markers: route.markers.filter(marker => marker.id !== removedMarker.pointId)
                    };
                }
                return route;
            });
            setRoutes(newRoutes);
            setAndFitMarkerBounds(prev =>
                prev.filter(marker => marker.id !== removedMarker.pointId)
            );
        }
    };

    const handleUpdateRoute = (event: google.maps.MapMouseEvent) => {
        routeUpdater({
            routes,
            zoom,
            event,
            geometryLibrary
        });
    };

    const onMarkerClick = useCallback((arg: InfoWindowType) => {
        setMarkers(prev =>
            prev.map(marker =>
                marker.id === arg.point.id && !marker.isEnd && !marker.isStart
                    ? {
                          ...marker,
                          selected: !marker.selected
                      }
                    : marker
            )
        );
        // if we need  show some error message when user try to select markers from different routes we can add dialog this
    }, []);

    const onAllMarkersSelectChange = useCallback(value => {
        setAndFitMarkerBounds(prev =>
            prev.map(marker => ({
                ...marker,
                selected: value
            }))
        );
    }, []);

    const onMarkerMove = useCallback(
        (event: google.maps.MapMouseEvent, pointId: string, routeId: string) => {
            if (!dndState.movingRecords || dndState.movingRecords.length === 0) {
                let newCheckedMarkers = [];

                const set = new Set(markers.filter(marker => marker.selected));
                newCheckedMarkers = Array.from(set);

                if (newCheckedMarkers.length > 0) {
                    dndState?.setMovingRecords(newCheckedMarkers);
                }
            }

            dndState?.setCoordinates([
                (event.domEvent as MouseEvent).pageX,
                (event.domEvent as MouseEvent).pageY
            ]);
            dndState?.setIsMoving(true);

            eventHandlerMap(event, map, handleUpdateRoute, removeRoute, routeId, pointId);
        },
        [dndState, markers]
    );

    const onZoom = useCallback(e => {
        setZoom(e.detail.zoom);
    }, []);

    const toggleShowAddress = (value: boolean) => {
        const updatedValue = {...mapSettings, showAddresses: value};
        updateADPmapSettings(updatedValue);
        setMapSettings(updatedValue);
    };

    const toggleShowLine = (value: boolean) => {
        const updatedValue = {...mapSettings, showLine: value};
        updateADPmapSettings(updatedValue);
        setMapSettings(updatedValue);
    };
    const toggleShowStartEnd = (value: boolean) => {
        const updatedValue = {...mapSettings, showStartEnd: value};
        updateADPmapSettings(updatedValue);
        setMapSettings(updatedValue);
    };

    const onMarkerDragStart = useCallback(
        (_, pointId: string, routeId: string) => {
            dndState.setIsMoving(true);
            if (markers.filter(marker => marker.selected).length === 0) {
                dndState.setMovingRecords(markers.filter(marker => marker.id === pointId) as any);
            } else {
                dndState.setMovingRecords(markers.filter(marker => marker.selected) as any);
            }

            const marker = markers.find(x => x.id === pointId);

            dndState.setFrom({
                id,
                group: routeId,
                row: Number.parseInt(marker.label)
            });
        },
        [dndState, markers]
    );

    useEffect(() => {
        for (const key of Object.keys(directionsRenderers.current)) {
            if (!routes.find(x => x.id === key)?.isSelected) {
                directionsRenderers.current[key]?.setMap(null);
                delete directionsRenderers.current[key];
            }
        }
    }, [routes]);

    return (
        <div ref={wrapperRef} className={styles.wrapper} id={MAP_CONTAINER_ID}>
            <GoogleMap
                mapTypeControl={false}
                mapTypeId="roadmap"
                className={styles.map}
                scrollwheel
                disableDoubleClickZoom
                gestureHandling="auto"
                defaultZoom={12}
                maxZoom={18}
                onZoomChanged={onZoom}
                defaultCenter={markers?.[0] || center}
                onCenterChanged={e =>
                    !dndState.isMoving ? setCurrentCenter(e.detail.center) : undefined
                }
                mapId={mapID}
                style={{width: '100%', height: '600px'}}
                streetViewControl={false}
                center={dndState.isMoving ? currentCenter : undefined}
            >
                {routes?.map((route, index) => (
                    <Route
                        key={`{${route.id}${route.data.getField(
                            route.locationSubform?.getColumnByName('KLINE').id
                        )}${mapSettings.showStartEnd}${mapSettings.showLine}}`}
                        color={route.color}
                        isShowStartEnd={mapSettings.showStartEnd}
                        startMarker={
                            mapSettings.showLine && route.isSelected ? route.startMarker : null
                        }
                        endMarker={
                            mapSettings.showLine && route.isSelected ? route.endMarker : null
                        }
                        coordinates={mapSettings.showLine && route.isSelected ? route.markers : []}
                        routeId={route.id}
                        directionsRenderers={directionsRenderers}
                        map={map}
                    />
                ))}

                {markers.map((marker, index) =>
                    marker.lat && marker.lng ? (
                        <Marker
                            onClick={onMarkerClick}
                            routeId={marker.route?.id || (-marker.record?.recordIndex).toString()}
                            key={
                                marker.route?.id +
                                marker.id +
                                mapSettings.showAddresses +
                                mapSettings.showStartEnd +
                                index
                            }
                            marker={marker}
                            onDragEnd={onMarkerMove}
                            dndState={dndState}
                            onDragStart={onMarkerDragStart}
                            setHoveredMarker={setHoveredMarker}
                        >
                            {mapSettings.showAddresses ? (
                                <>
                                    <div className={styles.fixedTooltip}>
                                        {marker.address || marker.label}
                                    </div>
                                    <MarkerIcon marker={marker} />
                                </>
                            ) : (
                                <ModernTooltip
                                    key={marker.id}
                                    tooltip={marker.address || marker.label}
                                    maxWidth={600}
                                    arrowOffset={8}
                                    position="top-start"
                                    tooltipOffset={8}
                                >
                                    <MarkerIcon marker={marker} />
                                </ModernTooltip>
                            )}
                        </Marker>
                    ) : null
                )}
            </GoogleMap>
            {dndState &&
                dndState.isMoving &&
                dndState.from?.id === id &&
                dndState.coordinates !== undefined &&
                (createPortal(
                    <div
                        className={styles.moving}
                        style={{
                            left: dndState.coordinates[0] + 5,
                            top:
                                dndState.coordinates[1] -
                                document.body.offsetTop -
                                DND_PLACEHOLDER_HEIGHT / 2
                        }}
                    >
                        <div className={styles.placeholder}>
                            <Text skin={'paragraph-2'} textColor={'primary-500'}>
                                {texts['drag_x_items'].replace(
                                    'X',
                                    dndState.movingRecords?.length || 1
                                )}
                            </Text>
                        </div>
                    </div>,
                    document.body
                ) as React.ReactNode)}
            <HamburgerMenu
                componentClasses={styles.hamburger}
                usePortal={true}
                portalId={MAP_CONTAINER_ID}
                onShowAddress={toggleShowAddress}
                onShowLines={toggleShowLine}
                onShowStartEnd={onShowStartEndHandler}
                mapSettings={mapSettings}
            />
            <Flex justifyContent="flex-start" style={{gap: '8px'}} componentClasses={styles.footer}>
                <Text skin="caption-1" textColor="secondary-500">
                    {markers.filter(m => m.selected).length}&nbsp;{texts.selected_dest}
                </Text>
                <Button skin="text" onClick={() => onAllMarkersSelectChange(false)}>
                    {texts.deselect}
                </Button>
            </Flex>
        </div>
    );
};

const MarkerIcon = ({marker}) => {
    return (
        <MarkerIconWithWrapper
            pathProps={{fill: marker.color || '#A142FF'}}
            isEmpty={marker.isStart || marker.isEnd}
            innerComp={
                marker.isStart || marker.isEnd ? (
                    <Icon icon={marker.isStart ? PriorityIcon.flagWhite : PriorityIcon.flagBlack} />
                ) : marker.selected ? (
                    <Icon icon={PriorityIcon.doneSmallOutline} iconColor="white" />
                ) : (
                    <Text
                        style={{
                            color: marker.color ? 'white' : 'black'
                        }}
                    >
                        {marker.label}
                    </Text>
                )
            }
        />
    );
};

export const MapWithDnd: FC<Omit<MapProps, 'dndState'>> = props => {
    const dndState = useDNDContext();
    return <Map {...props} dndState={dndState} />;
};
